"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import ImageTag from "../components/reuseable/imageTag";
import { UserLogoutAction } from "../Store/Actions/AuthActions";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  LogOut,
} from "lucide-react";

// ✅ Menu Items Array with Multiple Paths
const menuItems = [
  {
    title: "Dashboard",
    icon: "/assets/images/icons/home.svg",
    activeIcon: "/assets/images/icons/home-active.svg",
    paths: ["/dashboard"],
  },
  {
    title: "Users",
    icon: "/assets/images/icons/user.svg",
    activeIcon: "/assets/images/icons/user-active.svg",
    paths: ["/dashboard/users/approve-users", "/dashboard/users/nonprofit-users", "/dashboard/users/pin-users"],
    hasSubmenu: true,
    submenu: [
      {
        title: "User Approval",
        path: "/dashboard/users/approve-users",
      },
      {
        title: "All Non-Profit Users",
        path: "/dashboard/users/nonprofit-users",
      },
      {
        title: "All People-In-Need Users",
        path: "/dashboard/users/pin-users",
      },
    ],
  },
  // Role Manager — commented out, not needed for now
  // {
  //   title: "Role Manager",
  //   icon: "/assets/images/icons/users-roles.svg",
  //   activeIcon: "/assets/images/icons/users-roles-active.svg",
  //   paths: ["/dashboard/role-managment"],
  // },
  {
    title: "Announcement Manager",
    icon: "/assets/images/icons/announcement.svg",
    activeIcon: "/assets/images/icons/announcement-active.svg",
    paths: ["/dashboard/announcements"],
  },
  // {
  //   title: "Event Master List",
  //   icon: "/assets/images/icons/event-master.svg",
  //   activeIcon: "/assets/images/icons/event-master-active.svg",
  //   paths: ["/dashboard/event-master-list"],
  // },
  // {
  //   title: "Compliance Tracker",
  //   icon: "/assets/images/icons/tracker.svg",
  //   activeIcon: "/assets/images/icons/tracker-active.svg",
  //   paths: ["/dashboard/compliance"],
  // },
  // {
  //   title: "Content Review",
  //   icon: "/assets/images/icons/review.svg",
  //   activeIcon: "/assets/images/icons/review-active.svg",
  //   paths: ["/dashboard/content-review"],
  // },
  // {
  //   title: "Audit Log",
  //   icon: "/assets/images/icons/log.svg",
  //   activeIcon: "/assets/images/icons/log-active.svg",
  //   paths: ["/dashboard/audit-logs"],
  // },
  {
    title: "Reports Dashboard",
    icon: "/assets/images/icons/reports.svg",
    activeIcon: "/assets/images/icons/reports-active.svg",
    paths: ["/dashboard/analytics-report"],
  },
  {
    title: "Admin Inquiries",
    icon: "/assets/images/icons/review.svg",
    activeIcon: "/assets/images/icons/review-active.svg",
    paths: ["/dashboard/admin-inquiries"],
  },
  {
    title: "Get In Touch Queries",
    icon: "/assets/images/icons/get-in-touch.svg",
    activeIcon: "/assets/images/icons/get-in-touch-active.svg",
    paths: ["/dashboard/get-in-touch-queries"],
  },
];

const Sidebar = ({ isMobileMenuOpen, setIsMobileMenuOpen, isCollapse }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const [expandedMenus, setExpandedMenus] = useState({});
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      // Dispatch Redux logout action
      dispatch(UserLogoutAction());
      // Navigate to login page
      navigate("/");
    } catch (error) {
      console.error("Logout Error:", error);
    }
  };

  // ✅ Corrected Active State Logic
  const isActive = (paths) => paths.some((path) => location.pathname === path);

  // Check if a menu item with submenu should be expanded (if any submenu item is active)
  const shouldExpandMenu = (item) => {
    if (!item.hasSubmenu) return false;
    return item.submenu?.some((subItem) => location.pathname === subItem.path);
  };

  // Toggle submenu expansion
  const toggleSubmenu = (title) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  // Initialize expanded state based on active route
  useEffect(() => {
    menuItems.forEach((item) => {
      if (
        item.hasSubmenu &&
        item.submenu?.some((subItem) => location.pathname === subItem.path)
      ) {
        setExpandedMenus((prev) => ({
          ...prev,
          [item.title]: true,
        }));
      }
    });
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
      setIsOpen(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
    isCollapse(isOpen);
  };

  const sidebarVariants = {
    open: { width: "24%" },
    closed: { width: "80px", minWidth: "80px" },
  };

  const menuItemVariants = {
    open: { opacity: 1, x: 0 },
    closed: { opacity: 0, x: -10 },
  };

  const mobileSidebarVariants = {
    open: { y: 0, opacity: 1 },
    closed: { y: "-15%", opacity: 0 },
  };

  return (
    <AnimatePresence>
      {(isLargeScreen || isMobileMenuOpen) && (
        <motion.aside
          className={`fixed top-0 left-0 z-40 h-screen w-full bg-[#FDFDFD] shadow-md overflow-y-auto panelScroll ${
            isLargeScreen ? "" : "w-full"
          }`}
          initial={isLargeScreen ? "open" : "closed"}
          animate={isLargeScreen ? (isOpen ? "open" : "closed") : "open"}
          exit="closed"
          variants={isLargeScreen ? sidebarVariants : mobileSidebarVariants}
        >
          <div className="">
            <div
              className={`flex items-center bg-darkTheme ${
                isOpen ? "justify-between" : "justify-center py-4"
              } px-2 border-b border-[#FFFFFF1A]`}
            >
              {isOpen && (
                <motion.div
                  variants={menuItemVariants}
                  className="w-full flex justify-center"
                >
                  <ImageTag
                    path="/assets/logo.png"
                    classes="w-[250px] lg:w-[300px]"
                    altText="login"
                  />
                </motion.div>
              )}
              {isLargeScreen && (
                <button
                  onClick={toggleSidebar}
                  className={`p-1 rounded-full bg-themeColor text-white ${
                    isOpen ? "xl:-mt-8 2xl:-mt-12" : ""
                  }`}
                >
                  {isOpen ? <ChevronLeft /> : <ChevronRight />}
                </button>
              )}
              {!isLargeScreen && (
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-full text-white"
                >
                  <X />
                </button>
              )}
            </div>
            <nav className="space-y-2 mt-2 xl:mt-3 2xl:mt-4">
              {menuItems.map((item) => {
                const isItemActive = isActive(item.paths);
                const isExpanded =
                  expandedMenus[item.title] || shouldExpandMenu(item);
                const hasSubmenu =
                  item.hasSubmenu && (isOpen || !isLargeScreen);

                return (
                  <div key={item.title} className="lg:w-[90%] mx-auto">
                    <div
                      className={`
                        group relative flex items-center font-popinsSemiBold ${
                          !isOpen ? "px-4" : "px-6"
                        } py-2 2xl:py-2.5 rounded-lg
                        transition-all duration-300 ease-in-out
                        ${
                          isItemActive
                            ? "bg-themeColor !text-white"
                            : `hover:bg-themeColor hover:text-white ${
                                !isOpen ? "" : ""
                              }`
                        }  `}
                    >
                      {item.hasSubmenu ? (
                        <div
                          className="flex items-center w-full cursor-pointer"
                          onClick={() => {
                            if (hasSubmenu) {
                              toggleSubmenu(item.title);
                            }
                            if (!isLargeScreen) setIsMobileMenuOpen(false);
                          }}
                        >
                          {/* Gradient Bar */}
                          <span
                            className={`absolute right-0 top-1/2 -translate-y-1/2 w-[4px] rounded-r-md bg-darkTheme transition-all duration-300 delay-75 ease-in-out  ${
                              isItemActive
                                ? "h-[70%]"
                                : "h-0 group-hover:h-[70%]"
                            }`}
                          ></span>

                          {/* Icon */}
                          <div
                            className={`xl:p-1.5 2xl:p-2 rounded-lg
                              ${
                                isItemActive ? "bg-white" : "bg-themeColor"
                              } group-hover:bg-white
                            `}
                          >
                            <img
                              src={isItemActive ? item.activeIcon : item.icon}
                              className={`xl:size-4 2xl:size-5 transition-all duration-300 ease-in-out group-hover:hidden`}
                              alt=""
                            />
                            <img
                              src={item.activeIcon}
                              className={`xl:size-4 2xl:size-5 hidden group-hover:block transition-all duration-300 ease-in-out`}
                              alt=""
                            />
                          </div>

                          {/* Label */}
                          {(isOpen || !isLargeScreen) && (
                            <motion.span
                              variants={menuItemVariants}
                              className="pl-3 text-sm 2xl:text-base whitespace-nowrap flex-1"
                            >
                              {item.title}
                            </motion.span>
                          )}

                          {/* Chevron Icon for Submenu */}
                          {hasSubmenu && (
                            <motion.div
                              animate={{ rotate: isExpanded ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ChevronDown className="xl:size-4 2xl:size-5" />
                            </motion.div>
                          )}
                        </div>
                      ) : (
                        <Link
                          to={item.paths[0]}
                          className="flex items-center w-full"
                          onClick={() =>
                            !isLargeScreen && setIsMobileMenuOpen(false)
                          }
                        >
                          {/* Gradient Bar */}
                          <span
                            className={`absolute right-0 top-1/2 -translate-y-1/2 w-[4px] rounded-r-md bg-darkTheme transition-all duration-300 delay-75 ease-in-out  ${
                              isItemActive
                                ? "h-[70%]"
                                : "h-0 group-hover:h-[70%]"
                            }`}
                          ></span>

                          {/* Icon */}
                          <div
                            className={`xl:p-1.5 2xl:p-2 rounded-lg
                              ${
                                isItemActive ? "bg-white" : "bg-themeColor"
                              } group-hover:bg-white
                            `}
                          >
                            <img
                              src={isItemActive ? item.activeIcon : item.icon}
                              className={`xl:size-4 2xl:size-5 transition-all duration-300 ease-in-out group-hover:hidden`}
                              alt=""
                            />
                            <img
                              src={item.activeIcon}
                              className={`xl:size-4 2xl:size-5 hidden group-hover:block transition-all duration-300 ease-in-out`}
                              alt=""
                            />
                          </div>

                          {/* Label */}
                          {(isOpen || !isLargeScreen) && (
                            <motion.span
                              variants={menuItemVariants}
                              className="pl-3 text-sm 2xl:text-base whitespace-nowrap flex-1"
                            >
                              {item.title}
                            </motion.span>
                          )}
                        </Link>
                      )}
                    </div>

                    {/* Submenu Items */}
                    {hasSubmenu && isExpanded && (
                      <AnimatePresence>
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="pl-8 pt-1 space-y-1">
                            {item.submenu.map((subItem) => {
                              const isSubItemActive =
                                location.pathname === subItem.path;
                              return (
                                <Link
                                  key={subItem.path}
                                  to={subItem.path}
                                  className={`
                                    group relative flex items-center font-popinsSemiBold px-4 py-2 rounded-lg
                                    transition-all duration-300 ease-in-out
                                    ${
                                      isSubItemActive
                                        ? "bg-themeColor !text-white"
                                        : "hover:bg-themeColor hover:text-white text-gray-700"
                                    }
                                  `}
                                  onClick={() =>
                                    !isLargeScreen && setIsMobileMenuOpen(false)
                                  }
                                >
                                  {/* Gradient Bar for Submenu */}
                                  <span
                                    className={`absolute right-0 top-1/2 -translate-y-1/2 w-[4px] rounded-r-md bg-darkTheme transition-all duration-300 delay-75 ease-in-out  ${
                                      isSubItemActive
                                        ? "h-[70%]"
                                        : "h-0 group-hover:h-[70%]"
                                    }`}
                                  ></span>

                                  {/* Submenu Label */}
                                  <span className="text-sm 2xl:text-base whitespace-nowrap">
                                    {subItem.title}
                                  </span>
                                </Link>
                              );
                            })}
                          </div>
                        </motion.div>
                      </AnimatePresence>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
          <div className="py-4 space-y-2  w-[90%] mx-auto">
            <div
              className={`group relative flex items-center font-popinsSemiBold px-2 ${
                !isOpen ? "lg:px-4" : "hover:pl-9 lg:px-6"
              }  py-2.5
    transition-all duration-300 ease-in-out hover:bg-themeColor hover:text-white cursor-pointer rounded-lg`}
              onClick={handleLogout}
            >
              <span
                className={`absolute left-0 top-1/2 -translate-y-1/2 w-[4px] rounded-r-md bg-darkTheme transition-all duration-300 ease-in-out
                 h-0 group-hover:h-[70%]
                `}
              ></span>
              <div className="bg-themeColor p-2 rounded-lg text-white group-hover:bg-white group-hover:text-themeColor">
                <LogOut className="size-4 2xl:size-5" />
              </div>
              {isOpen && (
                <motion.span
                  variants={menuItemVariants}
                  className="pl-3 text-sm 2xl:text-base"
                >
                  Log Out
                </motion.span>
              )}
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
};

export default Sidebar;
